import React from "react";

import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";

type Props = {
  children: React.ReactNode;
};

type State = {
  hasError: boolean;
  errorMessage: string;
  componentStack: string;
};

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = {
    hasError: false,
    errorMessage: "",
    componentStack: "",
  };

  static getDerivedStateFromError(error: unknown): State {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error";
    return { hasError: true, errorMessage, componentStack: "" };
  }

  componentDidCatch(error: unknown, info: unknown) {
    console.error("UI crashed:", error, info);
    const componentStack =
      typeof info === "object" &&
      info !== null &&
      "componentStack" in info &&
      typeof (info as { componentStack?: unknown }).componentStack === "string"
        ? ((info as { componentStack: string }).componentStack || "")
        : "";

    if (componentStack && componentStack !== this.state.componentStack) {
      // Safe to setState here; only runs after an error and we guard against loops
      this.setState({ componentStack });
    }
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="h-full w-full flex items-center justify-center bg-background p-4">
        <Card className="max-w-xl w-full">
          <CardHeader>
            <CardTitle className="text-lg">Something went wrong</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-sm text-muted-foreground">
              The UI hit a runtime error. If you share the error message below,
              we can pinpoint the exact component causing it.
            </div>
            <pre className="text-xs rounded-md border border-border bg-muted/40 p-3 overflow-auto">
              {this.state.errorMessage}
            </pre>
            {this.state.componentStack && (
              <pre className="text-xs rounded-md border border-border bg-muted/40 p-3 overflow-auto whitespace-pre-wrap">
                {this.state.componentStack}
              </pre>
            )}
            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={() => window.location.reload()}
              >
                Reload
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
}


